import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, FormControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { pairwise } from 'rxjs';
import { DateInput } from '../../types/date-input';

export function dateValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    return control.value.getTime() === new Date(0).getTime()
      ? { dateValidator: { value: control.value } }
      : null;
  };
}

@Component({
  selector: 'date-input',
  template: require('./date-input.component.html'),
})
export class DateInputComponent implements OnChanges {
  @Input() label: string = '';
  @Input() date: DateInput = {
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    day: new Date().getDate(),
  };
  @Input() disabled = false;
  @Input() jumpToStart: boolean = false;
  @Output() dateChange = new EventEmitter<DateInput>();

  formControlYear = new FormControl(new Date().getFullYear());
  formControlMonth = new FormControl(new Date().getMonth() + 1);
  formControlDay = new FormControl(new Date().getDate());
  formControl = new FormControl(new Date(), [dateValidator()]);

  matcher = new ErrorStateMatcher();

  ngOnChanges(changes: SimpleChanges): void {
    this.formControl.setValue(
      new Date(this.date.year, this.date.month - 1, this.date.day)
    );
    this.formControlYear.setValue(this.date.year);
    this.formControlMonth.setValue(this.date.month);
    this.formControlDay.setValue(this.date.day);
    if (this.disabled) {
      this.formControl.disable();
      this.formControlYear.disable();
      this.formControlMonth.disable();
      this.formControlDay.disable();
    } else {
      this.formControl.enable();
      this.formControlYear.enable();
      this.formControlMonth.enable();
      this.formControlDay.enable();
    }
  }

  constructor() {
    this.formControlYear.valueChanges
      .pipe(pairwise())
      .subscribe(([prev, next]) => {
        if (prev !== next) {
          this.updateDate(
            next,
            this.formControlMonth.value!,
            this.formControlDay.value!
          );
          if (this.jumpToStart) {
            this.formControlMonth.setValue(1);
            this.formControlDay.setValue(1);
          }
        }
      });
    this.formControlMonth.valueChanges.subscribe((val) =>
      this.updateDate(
        this.formControlYear.value!,
        val,
        this.formControlDay.value!
      )
    );
    this.formControlDay.valueChanges.subscribe((val) =>
      this.updateDate(
        this.formControlYear.value!,
        this.formControlMonth.value!,
        val
      )
    );
    this.formControl.valueChanges.subscribe((val) => {
      if (val !== null) {
        if (val.getTime() !== new Date(0).getTime()) {
          this.formControlYear.setValue(val.getFullYear(), {
            emitEvent: false,
          });
          this.formControlMonth.setValue(val.getMonth() + 1, {
            emitEvent: false,
          });
          this.formControlDay.setValue(val.getDate(), { emitEvent: false });
          this.date.year = val.getFullYear();
          this.date.month = val.getMonth() + 1;
          this.date.day = val.getDate();
          this.dateChange.emit(this.date);
        }
      }
    });
  }

  updateDate(year: number | null, month: number | null, day: number | null) {
    if (year !== null && month !== null && day !== null) {
      if (!this.validateDate(year, month, day)) {
        this.formControl.setValue(new Date(0));
      } else {
        this.formControl.setValue(new Date(year, month - 1, day));
      }
      this.date.year = year;
      this.date.month = month;
      this.date.day = day;
      this.formControl.markAsTouched();
    }
  }

  validateDate(year: number, month: number, day: number) {
    let month_length = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

    if (year % 400 === 0 || (year % 100 != 0 && year % 4 == 0)) {
      month_length[1] = 29;
    }

    if (
      month >= 1 &&
      month <= 12 &&
      day > 0 &&
      day <= month_length[month - 1] &&
      year > 99
    ) {
      return true;
    }
    return false;
  }

  checkDateChar(event: any) {
    let regex = new RegExp('[0-9]');
    let key = String.fromCharCode(
      !event.charCode ? event.which : event.charCode
    );
    if (!regex.test(key)) {
      event.preventDefault();
      return false;
    }
    return true;
  }
}
